import { FileText, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { ORG_NOTE_MAX_LENGTH, type OrgNoteDraft } from "./use-org-note-draft";

// The trigger stands out once a note exists so reviewers notice it.
const TRIGGER_WITH_NOTE = {
  className:
    "h-8 gap-1.5 whitespace-nowrap border-blue-200 bg-blue-50 px-2 text-xs font-medium text-blue-700 hover:bg-blue-100 hover:text-blue-800",
  Icon: FileText,
  label: "View org note",
};

const TRIGGER_WITHOUT_NOTE = {
  className: "h-8 gap-1.5 whitespace-nowrap px-2 text-xs text-slate-600",
  Icon: Plus,
  label: "Add org note",
};

interface OrgNotePopoverProps {
  orgNote: string | null;
  canEdit: boolean;
  open: boolean;
  isSaving: boolean;
  draft: OrgNoteDraft;
}

/** Compact org note: a trigger button that opens the note in a popover. */
export function OrgNotePopover({
  orgNote,
  canEdit,
  open,
  isSaving,
  draft,
}: OrgNotePopoverProps) {
  const trigger = orgNote ? TRIGGER_WITH_NOTE : TRIGGER_WITHOUT_NOTE;

  return (
    <Popover open={open} onOpenChange={draft.handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={trigger.className}
        >
          <trigger.Icon className="h-3.5 w-3.5" />
          {trigger.label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3" align="end">
        <div>
          <p className="text-sm font-medium">Org Note</p>
          <p className="text-xs text-slate-500">Hidden from the provider</p>
        </div>
        {canEdit ? (
          <OrgNotePopoverForm
            orgNote={orgNote}
            isSaving={isSaving}
            draft={draft}
          />
        ) : (
          <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
            {orgNote}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

function OrgNotePopoverForm({
  orgNote,
  isSaving,
  draft,
}: {
  orgNote: string | null;
  isSaving: boolean;
  draft: OrgNoteDraft;
}) {
  const hasChanges = draft.note.trim() !== (orgNote ?? "");

  return (
    <>
      <Textarea
        aria-label="Org Note"
        value={draft.note}
        onChange={(event) => draft.setNote(event.target.value)}
        maxLength={ORG_NOTE_MAX_LENGTH}
        rows={4}
        placeholder="Add context for your business unit..."
        disabled={isSaving}
        className="resize-y text-sm"
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-slate-500">
          {draft.note.length.toLocaleString()} /{" "}
          {ORG_NOTE_MAX_LENGTH.toLocaleString()}
        </span>
        <Button
          type="button"
          size="sm"
          onClick={draft.saveNote}
          disabled={!hasChanges || isSaving}
        >
          {isSaving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
          Save note
        </Button>
      </div>
    </>
  );
}
